function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from 'react';
import PropTypes from 'prop-types';
import NukaCarousel from '@domain-group/fe-allhomes-nuka-carousel';
import ImageIndex from './image-index';
import LazyLoadedChild from './lazy-loaded-child';
import * as styles from './allhomes-hero-carousel.style';

// These callbacks ensure that a mouse drag isn't treated as a click. The number of pixels travelled must be
// less than the value of this.props.dragThreshold to count as a click.
import { jsx as ___EmotionJSX } from "@emotion/core";
function makeMouseDragCallbacks(frame, dragThreshold) {
  var mouseDown = false;
  var origin = [];
  var distanceMoved = 0;
  return {
    onMouseDown: event => {
      mouseDown = true;
      origin = [event.pageX, event.pageY];
      distanceMoved = 0;
    },
    onMouseMove: event => {
      if (mouseDown) {
        var delta = Math.abs(origin[0] - event.pageX) + Math.abs(origin[1] - event.pageY);
        distanceMoved += delta;
        origin = [event.pageX, event.pageY];
      }
    },
    onClickCapture: event => {
      if (distanceMoved >= dragThreshold) {
        event.stopPropagation();
      }
      mouseDown = false;
      distanceMoved = 0;
    }
  };
}

// This is the small "1 / 4" image number indicator in the bottom right.
var imageIndexDecorator = {
  component: ImageIndex,
  position: 'BottomRight'
};
var nukaDefaultSettings = {
  edgeEasing: 'easeOutExpo',
  swiping: true
};
class AllhomesHeroCarousel extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "childRefs", []);
    this.displayedImage = 0;
    this.onFrameDisplayed = this.onFrameDisplayed.bind(this);
    this.loadVisibleImages = this.loadVisibleImages.bind(this);
  }
  componentDidMount() {
    this.loadVisibleImages();
  }
  componentDidUpdate(prevProps) {
    // Recheck the lazy-loading status if the browser is enlarged
    if (this.props.imagesToDisplay > prevProps.imagesToDisplay) {
      this.loadVisibleImages();
    }
  }
  onFrameDisplayed(frameNumber) {
    this.loadVisibleImages(frameNumber);
    this.displayedImage = frameNumber;
  }
  loadVisibleImages() {
    var frameNumber = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.displayedImage;
    // Load the visible set of images plus N additional ones to either side (N is lazyLoadPadding prop)
    var childCount = React.Children.count(this.props.children);
    for (var index = -this.props.lazyLoadPadding; index < this.props.imagesToDisplay + this.props.lazyLoadPadding; index += 1) {
      this.childRefs[(frameNumber + index + childCount) % childCount].load();
    }
  }
  render() {
    // Wrap children in LazyLoadedChild containers so they can be displayed only when they're almost visible.
    /* eslint-disable react/no-array-index-key */
    var children = React.Children.map(this.props.children, (child, index) => ___EmotionJSX(LazyLoadedChild, _extends({
      key: index
    }, makeMouseDragCallbacks(child, this.props.dragThreshold), {
      ref: _ref => {
        this.childRefs[index] = _ref;
      },
      isStatic: this.props.firstImageStatic && index === 0
    }), child));
    /* eslint-enable react/no-array-index-key */

    // Set up the NukaCarousel props
    var nukaSettings = _objectSpread({}, nukaDefaultSettings);
    nukaSettings.decorators = [...this.props.decorators];
    if (this.props.showCounter) {
      nukaSettings.decorators.push(imageIndexDecorator);
    }
    if (this.props.firstImageStatic) {
      nukaSettings.firstImageStatic = true;
    }
    nukaSettings.beforeSlide = (oldIndex, newIndex) => {
      if (oldIndex !== newIndex) {
        this.props.onImageChanged(oldIndex, newIndex);
      }
    };
    nukaSettings.afterSlide = this.onFrameDisplayed;
    nukaSettings.slidesToShow = this.props.imagesToDisplay;
    nukaSettings.wrapAround = this.props.infinite;
    nukaSettings.cellSpacing = this.props.imagePadding;
    return ___EmotionJSX("div", {
      css: styles.container
    }, ___EmotionJSX(NukaCarousel, nukaSettings, children));
  }
}
AllhomesHeroCarousel.propTypes = {
  showCounter: PropTypes.bool,
  decorators: PropTypes.arrayOf(PropTypes.shape({
    component: PropTypes.func.isRequired,
    position: PropTypes.oneOf(['TopLeft', 'TopCenter', 'TopRight', 'CenterLeft', 'CenterCenter', 'CenterRight', 'BottomLeft', 'BottomCenter', 'BottomRight']).isRequired
  })),
  dragThreshold: PropTypes.number,
  firstImageStatic: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element]).isRequired,
  imagesToDisplay: PropTypes.number,
  infinite: PropTypes.bool,
  imagePadding: PropTypes.number,
  lazyLoadPadding: PropTypes.number,
  onImageChanged: PropTypes.func
};
AllhomesHeroCarousel.defaultProps = {
  showCounter: true,
  decorators: [],
  dragThreshold: 8,
  firstImageStatic: false,
  imagesToDisplay: 1,
  infinite: false,
  imagePadding: 0,
  lazyLoadPadding: 1,
  onImageChanged: () => {}
};
export default AllhomesHeroCarousel;